import { NameCell } from '@openx/components-core/lib/NameCell/NameCell';
import {
  CriteriaDimension,
  type PaginationCriteria,
  type SortCriteria,
  SortDirection,
} from '@openx/types/tableCriteria';
import type { TableColumn } from '@openx/types/tableHelpers';
import { toUTCDateTime } from '@openx/utils/lib/date';
import { numberWithCommas } from '@openx/utils/lib/numberWithCommas';
import type { TFunction } from 'i18next';
import { TABLE_PAGE_SIZE } from '../../config';
import { SENSITIVE_CATEGORIES_TABS, type SensitiveCategoriesTabs } from './hooks/useSensitiveCategoriesTabs';
import type { DealAdCategoryProposal } from './model';
import type { DealsSensitiveCategoriesListFilters } from './types';

export const defaultSensitiveCategoriesListFitlers: DealsSensitiveCategoriesListFilters = {
  phrase: '',
  category: [],
};

export type SensitiveCategoriesListCriteria = {
  [CriteriaDimension.SORT]: SortCriteria;
  [CriteriaDimension.FILTERS]: DealsSensitiveCategoriesListFilters;
  [CriteriaDimension.PAGINATION]: PaginationCriteria;
};

const defaultCriteria: SensitiveCategoriesListCriteria = {
  [CriteriaDimension.SORT]: {
    column: '',
    direction: SortDirection.DESC,
    sortableColumns: ['dealName', 'organization', 'proposedAdCategory', 'categoryBids', 'categoryBidShare'],
  },
  [CriteriaDimension.PAGINATION]: {
    pageNumber: 1,
    pageSize: TABLE_PAGE_SIZE,
    totalCount: 0,
  },
  [CriteriaDimension.FILTERS]: {
    phrase: '',
    category: [],
  },
};

const defaultProposedSensitiveCategoriesCriteria = structuredClone(defaultCriteria);
defaultProposedSensitiveCategoriesCriteria.sort.column = 'proposedDate';
defaultProposedSensitiveCategoriesCriteria.sort.sortableColumns.push('proposedDate');

const defaultDecidedSensitiveCategoriesCriteria = structuredClone(defaultCriteria);
defaultDecidedSensitiveCategoriesCriteria.sort.column = 'decisionDate';
defaultDecidedSensitiveCategoriesCriteria.sort.sortableColumns.push('decisionDate');

export { defaultDecidedSensitiveCategoriesCriteria, defaultProposedSensitiveCategoriesCriteria };

export const getBaseDealsSensitiveCategoriesColumns = (t: TFunction): TableColumn<DealAdCategoryProposal>[] => [
  {
    key: 'dealName',
    title: t('Name'),
    render: row => <NameCell id={row.id} name={row.deal?.name ?? row.dealId} />,
  },
  {
    key: 'organization',
    title: t('Organization'),
    render: row => row.deal?.account?.name,
  },
  {
    key: 'proposedAdCategory',
    title: t('Proposed Category'),
    render: row => row.proposedAdCategory,
  },
  {
    key: 'categoryBids',
    title: t('Bids in Category'),
    render: row => {
      return numberWithCommas(row.categoryBids);
    },
  },
  {
    key: 'categoryBidShare',
    title: t('Share of Category'),
    render: row => {
      return `${(row.categoryBidShare * 100).toFixed(0)}%`;
    },
  },
];

export const getDealsProposedSensitiveCategoriesColumns = (t: TFunction): TableColumn<DealAdCategoryProposal>[] => [
  ...getBaseDealsSensitiveCategoriesColumns(t),
  {
    key: 'proposedDate',
    title: t('Submission Date'),
    render: ({ proposedDate }) => toUTCDateTime(proposedDate),
  },
];

export const getDealsDecidedSensitiveCategoriesColumns = (
  activeTab: SensitiveCategoriesTabs,
  t: TFunction
): TableColumn<DealAdCategoryProposal>[] => [
  ...getBaseDealsSensitiveCategoriesColumns(t),
  {
    key: 'decisionDate',
    title: activeTab === SENSITIVE_CATEGORIES_TABS.Rejected ? t('Rejection Date') : t('Application Date'),
    render: ({ decisionDate }) => (decisionDate ? toUTCDateTime(decisionDate) : null),
  },
];
